import React, { useState } from "react";
import axios from "axios";
import { ArrowUpRight, Loader2, CheckCircle2, Video } from "lucide-react";
import { BRAND } from "../../lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL = {
    name: "",
    email: "",
    company: "",
    phone: "",
    preferred_date: "",
    preferred_time: "",
    timezone_str: "IST (GMT+5:30)",
    topic: "",
    notes: "",
};

const TIMEZONES = [
    "IST (GMT+5:30)",
    "GMT / UTC",
    "EST (GMT-5)",
    "CET (GMT+1)",
    "GST (GMT+4)",
    "WAT (GMT+1)",
    "SGT (GMT+8)",
];

export default function MeetingForm() {
    const [form, setForm] = useState(INITIAL);
    const [status, setStatus] = useState({ loading: false, ok: false, err: "" });

    const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setStatus({ loading: true, ok: false, err: "" });
        try {
            await axios.post(`${API}/meetings`, form);
            setStatus({ loading: false, ok: true, err: "" });
            setForm(INITIAL);
        } catch (err) {
            const msg =
                err?.response?.data?.detail?.[0]?.msg ||
                err?.response?.data?.detail ||
                "Unable to book right now. Please try again.";
            setStatus({
                loading: false,
                ok: false,
                err: typeof msg === "string" ? msg : "Submission failed.",
            });
        }
    };

    return (
        <section
            id="meeting"
            className="bg-[#0C4A6E] text-white py-24 md:py-32 relative overflow-hidden"
            data-testid="meeting-section"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 relative">
                <div className="md:col-span-5">
                    <div
                        className="font-mono text-[11px] uppercase tracking-[0.2em] mb-4"
                        style={{ color: "#BAE6FD" }}
                    >
                        06 / Book a Meeting
                    </div>
                    <h2 className="font-cabinet font-bold text-4xl md:text-5xl leading-[1.05] tracking-tight">
                        Meet our team
                        <br />
                        <span className="italic font-normal text-sky-200">
                            on Zoom.
                        </span>
                    </h2>
                    <p className="mt-6 text-lg text-sky-50/90 leading-relaxed">
                        Pick a date and time that works for your region. We'll
                        confirm your slot and send a Zoom invite to your
                        inbox — usually within a few working hours.
                    </p>

                    <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-3">
                        <a
                            href={BRAND.zoomUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-3 bg-white text-[#0C4A6E] px-6 py-4 hover:bg-sky-50 transition-colors"
                            data-testid="meeting-direct-zoom"
                        >
                            <Video size={18} strokeWidth={1.5} />
                            <span className="font-mono text-sm tracking-wider uppercase">
                                Join Zoom Room
                            </span>
                        </a>
                        <a
                            href="#meeting-form-anchor"
                            onClick={(e) => {
                                e.preventDefault();
                                document
                                    .getElementById("meeting-form-anchor")
                                    ?.scrollIntoView({ behavior: "smooth", block: "center" });
                            }}
                            className="inline-flex items-center gap-3 border border-sky-300/50 px-6 py-4 hover:bg-sky-900 transition-colors"
                            data-testid="meeting-scroll-to-form"
                        >
                            <span className="font-mono text-sm tracking-wider uppercase">
                                Request a Slot
                            </span>
                        </a>
                    </div>

                    <div className="mt-6 text-xs font-mono text-sky-100/80 uppercase tracking-[0.18em]">
                        Meeting ID: {BRAND.zoomMeetingId}
                    </div>

                    <div className="mt-10 pt-8 border-t border-sky-800/70 text-sm text-sky-100/80">
                        Prefer WhatsApp?{" "}
                        <a
                            className="underline underline-offset-4 text-white hover:text-sky-200"
                            href={`https://wa.me/${BRAND.whatsapp.replace("+", "")}`}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="meeting-whatsapp-link"
                        >
                            Chat with us on {BRAND.whatsappDisplay}
                        </a>
                    </div>
                </div>

                <div className="md:col-span-7">
                    <form
                        id="meeting-form-anchor"
                        onSubmit={submit}
                        className="bg-white text-ink p-6 md:p-10"
                        data-testid="meeting-form"
                    >
                        <div className="grid sm:grid-cols-2 gap-5">
                            <div>
                                <label className="field-label">
                                    Full Name *
                                </label>
                                <input
                                    required
                                    value={form.name}
                                    onChange={handle("name")}
                                    className="field-input"
                                    data-testid="meeting-name"
                                />
                            </div>
                            <div>
                                <label className="field-label">Email *</label>
                                <input
                                    required
                                    type="email"
                                    value={form.email}
                                    onChange={handle("email")}
                                    className="field-input"
                                    data-testid="meeting-email"
                                />
                            </div>
                            <div>
                                <label className="field-label">Company</label>
                                <input
                                    value={form.company}
                                    onChange={handle("company")}
                                    className="field-input"
                                    data-testid="meeting-company"
                                />
                            </div>
                            <div>
                                <label className="field-label">
                                    Phone / WhatsApp
                                </label>
                                <input
                                    value={form.phone}
                                    onChange={handle("phone")}
                                    className="field-input"
                                    data-testid="meeting-phone"
                                />
                            </div>
                            <div>
                                <label className="field-label">
                                    Preferred Date *
                                </label>
                                <input
                                    required
                                    type="date"
                                    value={form.preferred_date}
                                    onChange={handle("preferred_date")}
                                    className="field-input"
                                    data-testid="meeting-date"
                                />
                            </div>
                            <div>
                                <label className="field-label">
                                    Preferred Time *
                                </label>
                                <input
                                    required
                                    type="time"
                                    value={form.preferred_time}
                                    onChange={handle("preferred_time")}
                                    className="field-input"
                                    data-testid="meeting-time"
                                />
                            </div>
                            <div>
                                <label className="field-label">Timezone</label>
                                <select
                                    value={form.timezone_str}
                                    onChange={handle("timezone_str")}
                                    className="field-input bg-white"
                                    data-testid="meeting-timezone"
                                >
                                    {TIMEZONES.map((tz) => (
                                        <option key={tz} value={tz}>
                                            {tz}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="field-label">Topic</label>
                                <input
                                    value={form.topic}
                                    onChange={handle("topic")}
                                    className="field-input"
                                    placeholder="e.g. Pharma supply discussion"
                                    data-testid="meeting-topic"
                                />
                            </div>
                        </div>

                        <div className="mt-5">
                            <label className="field-label">
                                Notes (optional)
                            </label>
                            <textarea
                                value={form.notes}
                                onChange={handle("notes")}
                                className="field-input min-h-[100px]"
                                placeholder="Agenda, attendees, products to discuss..."
                                data-testid="meeting-notes"
                            />
                        </div>

                        {status.err && (
                            <div
                                className="mt-5 p-4 border border-red-300 bg-red-50 text-red-700 text-sm"
                                data-testid="meeting-error"
                            >
                                {status.err}
                            </div>
                        )}

                        {status.ok && (
                            <div
                                className="mt-5 p-4 border border-[#4D7C0F] bg-[#f2f8e9] text-[#3a5f08] text-sm flex items-start gap-3"
                                data-testid="meeting-success"
                            >
                                <CheckCircle2
                                    size={18}
                                    strokeWidth={1.8}
                                    className="mt-0.5"
                                />
                                <div>
                                    Meeting request received. We'll confirm and
                                    send the Zoom invite to your email shortly.
                                </div>
                            </div>
                        )}

                        <div className="mt-8 flex items-center justify-between gap-4 flex-wrap">
                            <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-stone-500">
                                * Required fields
                            </p>
                            <button
                                type="submit"
                                disabled={status.loading}
                                className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                                data-testid="meeting-submit"
                            >
                                {status.loading ? (
                                    <>
                                        <Loader2
                                            size={18}
                                            className="animate-spin"
                                            strokeWidth={1.5}
                                        />
                                        Booking
                                    </>
                                ) : (
                                    <>
                                        Book Zoom Meeting
                                        <ArrowUpRight
                                            size={18}
                                            strokeWidth={1.5}
                                        />
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </section>
    );
}
